/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import * as React from "react"

interface SEOProps {
  /**
   * The canonical URL for the page
   */
  canonicalUrl: string
  /**
   * Any additional elements to be rendered in the head
   */
  children?: React.ReactNode
  /**
   * The description of the page
   */
  description: string
  /**
   * The image to be used for social sharing. Images will be resized to 1200x630
   */
  image?: string
  /**
   * The alt text for the image
   */
  imageAltText?: string
  /**
   * The language code added to the `html` tag
   * @default en
   */
  lang?: string
  /**
   * Any additional meta tags to be rendered in the head
   */
  meta?: React.MetaHTMLAttributes<HTMLMetaElement>[]
  /**
   * The description of the page for social sharing. Defaults to the description prop if not provided
   * @default description
   */
  openGraphDescription?: string
  /**
   * The title of the page for social sharing. Defaults to the title prop if not provided
   * @default title
   */
  openGraphTitle?: string
  /**
   * The title of the page
   */
  title: string
  /**
   * The structured data to be rendered as JSON-LD under the mainEntity for the page
   */
  structuredData?: any[]
  /**
   * The type of page
   */
  type?: "faq" | "page"
}

function SEO({
  canonicalUrl,
  children,
  description,
  image,
  imageAltText,
  lang = `en`,
  meta = [],
  openGraphDescription,
  openGraphTitle,
  title,
  structuredData,
  type,
}: SEOProps) {
  const metaDescription = description

  let pageType

  switch (type) {
    case "faq":
      pageType = "FAQPage"
      break
    default:
      pageType = "WebPage"
      break
  }

  const defaultMeta = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:title`,
      content: openGraphTitle || title,
    },
    {
      property: `og:description`,
      content: openGraphDescription || metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: "@intralox",
    },
    {
      name: `twitter:title`,
      content: openGraphTitle || title,
    },
    {
      name: `twitter:description`,
      content: openGraphDescription || metaDescription,
    },
    image
      ? {
          content: `${image}?w=1200&h=630&fit=crop`,
          property: `og:image`,
        }
      : null,
    image
      ? {
          content: `1200`,
          property: `og:image:width`,
        }
      : null,
    image
      ? {
          content: `630`,
          property: `og:image:height`,
        }
      : null,
    imageAltText
      ? {
          content: `${imageAltText}`,
          property: `og:image:alt`,
        }
      : null,
  ].filter(Boolean)

  const allMeta = [...defaultMeta, ...meta]

  return (
    <React.Fragment>
      <html lang={lang} />
      <title>{title ? `${title} | ` : ``}Intralox</title>
      {allMeta.map((item, index) => (
        <meta key={index} {...item} />
      ))}
      {structuredData && structuredData.length ? (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": pageType,
            mainEntity: structuredData,
          })}
        </script>
      ) : null}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {children}
    </React.Fragment>
  )
}

export default SEO
